import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../../loaders';
import api from "../../../../services/api";
import { Button, Col, Container, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { useFlexLayout, useRowSelect, useTable } from 'react-table';
import { TooltipButton } from '../../../inputs';

function PositionListComponent(props) {
    const { id, title, data, onSelect, onMove, hideLabel, columnHeader, listPosition } = props;

    const columns = useMemo(() => [
        {
            Header: columnHeader,
            accessor: 'lookupValue',
        },
    ], []);

    const defaultColumn = useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 200,
        }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        toggleRowSelected,
    } = useTable (
        {
            columns,
            data,
            defaultColumn,
        },
        useFlexLayout,
        useRowSelect,
    );

    return (
        <>
            {!hideLabel && <label className="">{title}</label>}
            <div {...getTableProps()} className="table flex table-hover scrollable">
                <div className="thead">
                    {headerGroups.map(headerGroup => {
                        return (<div {...headerGroup.getHeaderGroupProps()} className="tr">
                            {headerGroup.headers.map((column, index) => {
                                return (<div {...column.getHeaderProps()} className="th">
                                    {column.render('Header')}
                                </div>);
                            })}
                            <div className="th th-action text-right" style={{width: "77px"}}></div>
                        </div>);
                    }
                    )}
                </div>
                <div {...getTableBodyProps()} className="tbody">
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <div {...row.getRowProps()} className="tr">
                            {
                                listPosition === "right" &&
                                <div className="td td-action text-left" style={{width: "60px"}}>
                                    <TooltipButton
                                        id={`move_${i}`}
                                        title="Move"
                                        className="btn-icon"
                                        color="default"
                                        size="sm"
                                        type="button"
                                        outline
                                        onClick={() => {
                                            onMove(row.original)
                                        }}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </TooltipButton>
                                </div>
                            }
                            {
                                row.cells.map(cell => {
                                    return <div {...cell.getCellProps({
                                        onClick: cell.column.id !== "selection" ? () => toggleRowSelected(row.id) : undefined
                                    })}
                                        className={`td ${cell.column.id !== "selection" ? "cursor-pointer" : ""}`}
                                    >
                                        {cell.render('Cell')}
                                    </div>;
                                })
                            }
                            {
                                listPosition === "left" &&
                                <div className="td td-action text-right" style={{width: "60px"}}>
                                    <TooltipButton
                                        id={`move_${i}`}
                                        title="Move"
                                        className="btn-icon"
                                        color="default"
                                        size="sm"
                                        type="button"
                                        outline
                                        onClick={() => {
                                            onMove(row.original)
                                        }}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </TooltipButton>
                                </div>
                            }
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

export default function PositionList(props) {
    const {
        milestoneTemplateId,
        onClose
    } = props;

    const [ loading, setLoading ] = useState(false);
    const [ milestoneTemplate, setMilestoneTemplate ] = useState(null);
    const [ selectedPositions, setSelectedPositions ] = useState([]);
    const [ positionName, setPositionName ] = useState(null);
    const [ positions, setPositions ] = useState([]);

    const loadData = useCallback(() => {
        setMilestoneTemplate(null);
        setSelectedPositions([]);

        if (!milestoneTemplateId)
            return;

        setLoading(true);

        const apiCalls = [
            api.get(`/milestonetemplate/${milestoneTemplateId}`),
            api.get(`/milestonetemplate/positions?id=${milestoneTemplateId}`)    
        ]

        Promise.all(apiCalls)
            .then((responses) => {
                setMilestoneTemplate(responses[0]?.data);
                setSelectedPositions(responses[1]?.data ?? []);
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false))
        ;
    }, [milestoneTemplateId]);

    useEffect(() => {
        loadData();
    }, [milestoneTemplateId]);

    const loadPositionData = useCallback(() => {
        setPositions([]);

        if (!positionName || positionName?.trim() === "")
            return;
        
        setLoading(true);
        api.get(`/lookup/position?notMappedToMilestone=true&filter=${positionName}`)
            .then((response) => {
                const loadedPositions = response.data?.filter(p => selectedPositions.filter(sp => sp.lookupId === p.lookupId).length === 0);
                setPositions(loadedPositions);
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false))
        ;
    }, [selectedPositions, positionName]);

    useEffect(() => {
        loadPositionData();
    }, [positionName]);

    const handleSave = useCallback(() => {
        if (!selectedPositions)
            return;
        
        setLoading(true);

        api.post(`/milestoneTemplate/positions/${milestoneTemplateId}`, selectedPositions.map(sp => sp.lookupId))
            .then(() => {
                setLoading(false);
                onClose();
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            })
        ;
    }, [milestoneTemplateId, selectedPositions, onClose]);

    return (
        <Modal
            isOpen={true}
            className="modal-lg"
            modalClassName="db-example-modal-lg"
        >
        {
            loading && <Loader />
        }
            <div className="modal-header">
                <h5 className="modal-title">
                    {milestoneTemplate?.templateName} Position
                </h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={() => onClose()}
                    type="button"
                >
                    <span aria-hidden={true}>x</span>
                </button>
            </div>
            <div className="modal-body">
                <Container fluid>
                    <Row>
                        <Col lg={6}>
                            <Row>
                                <Col lg={12}>
                                    <FormGroup>
                                        <Label>
                                            Available Position Name
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            value={positionName ?? ""}
                                            onChange={(e) => setPositionName(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={6}>
                            <PositionListComponent
                                id="available-positions"
                                title="Available Positions"
                                hideLabel={true}
                                data={positions}
                                onSelect={() => {}}
                                onMove={(data) => {
                                    const currentPositions = [...positions];
                                    const removedPosition = currentPositions.filter(sp => sp.lookupId === data.lookupId)[0];
                                    currentPositions.splice(currentPositions.indexOf(removedPosition), 1);
                                    setPositions(currentPositions);
                                    const currentSelectedPositions = [...selectedPositions];
                                    currentSelectedPositions.push(data);
                                    setSelectedPositions(currentSelectedPositions);
                                }}
                                columnHeader="Available Positions"
                                listPosition="left"
                            />
                        </Col>
                        <Col lg={6}>
                            <PositionListComponent
                                id="selected-positions"
                                title="Selected Positions"
                                hideLabel={true}
                                data={selectedPositions}
                                onSelect={() => {}}
                                onMove={(data) => {
                                    const currentSelectedPositions = [...selectedPositions];
                                    const removedPosition = currentSelectedPositions.filter(sp => sp.lookupId === data.lookupId)[0];
                                    currentSelectedPositions.splice(currentSelectedPositions.indexOf(removedPosition), 1);
                                    setSelectedPositions(currentSelectedPositions);

                                    if (!positionName || positionName.trim() === "" || !data?.lookupValue?.toLowerCase().includes(positionName?.toLowerCase()))
                                        return;

                                    const currentPositions = [...positions];
                                    currentPositions.push(data);
                                    setPositions(currentPositions);
                                }}
                                columnHeader="Selected Positions"
                                listPosition="right"
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className="modal-footer">
                <Button
                    color="secondary"
                    onClick={() => onClose()}
                    type="button"
                >
                    Close
                </Button>
                <Button
                    color="info"
                    onClick={() => handleSave()}
                    type="button"
                >
                    Save Changes
                </Button>
            </div>
        </Modal>
    );
};
